/**
 * ContentMetadataSidebar - Right collapsible sidebar for content metadata
 *
 * Tabbed interface with: Properties, Social Copy, Comments, Analytics.
 * Uses ReaderDetailPanelShell for visual consistency with DocOutline.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  SlidersHorizontal,
  Share2,
  MessageSquare,
  BarChart3,
  User,
  Tag,
  Star,
  Calendar as CalIcon,
  Trash2,
  Sparkles,
  Loader2,
  Check,
  RotateCcw,
  X,
  Settings,
  Clock,
  Send,
  Ban,
  ChevronDown,
  ChevronRight,
  List,
} from 'lucide-react';
import { DocOutlineContent } from '../../../components/documentation/DocOutline';
import { type EditorDocument } from '../../../api/documents';
import {
  contentApi,
  STATUS_LABELS,
  STATUS_COLORS,
  ALL_STATUSES,
  CONTENT_TYPE_LABELS,
  ALL_CONTENT_TYPES,
  SCHEDULE_STATUS_COLORS,
  SCHEDULE_STATUS_LABELS,
  type ContentItem,
  type ContentStatus,
  type ContentType,
  type ContentSchedule,
  type ContentVersion,
  type Author,
  type SocialCopy,
} from '../../../api/content';
import { TagInput } from '../../../components/shared/TagInput';
import { CommentsSection } from '../../../components/shared/CommentsSection';
import { ItemAnalyticsCard } from '../../../components/shared/ItemAnalyticsCard';
import { ReaderDetailPanelShell, type TabDefinition } from '../../../components/shared/ReaderDetailPanelShell';

const SOCIAL_CONTENT_TYPES: Set<ContentType> = new Set(['linkedin_post', 'social_post', 'thread']);

type TabType = 'properties' | 'social' | 'comments' | 'analytics' | 'history' | 'toc';

interface ContentMetadataSidebarProps {
  item: ContentItem;
  authors: Author[];
  socialCopies: SocialCopy[];
  projectId: string;
  workspaceId?: string;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onUpdateItem: (data: Partial<ContentItem>) => void;
  onSaveSocialCopy: (authorId: string, platform: string, text: string) => void;
  onDeleteItem: () => void;
  onReloadItem?: () => void;
  isSaving: boolean;
  document?: EditorDocument;
}

export function ContentMetadataSidebar({
  item,
  authors,
  socialCopies,
  projectId,
  workspaceId,
  isCollapsed,
  onToggleCollapse,
  onUpdateItem,
  onSaveSocialCopy,
  onDeleteItem,
  onReloadItem,
  isSaving,
  document: linkedDoc,
}: ContentMetadataSidebarProps) {
  const [activeTab, setActiveTab] = useState<TabType>('properties');

  // Version history state
  const [versions, setVersions] = useState<ContentVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<ContentVersion | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreSuccess, setRestoreSuccess] = useState(false);

  const tabs: TabDefinition[] = useMemo(() => [
    { id: 'properties', icon: <SlidersHorizontal size={16} />, title: 'Properties' },
    { id: 'social', icon: <Share2 size={16} />, title: 'Social Copy' },
    { id: 'comments', icon: <MessageSquare size={16} />, title: 'Comments' },
    { id: 'analytics', icon: <BarChart3 size={16} />, title: 'Analytics' },
    { id: 'history', icon: <Clock size={16} />, title: 'History' },
    { id: 'toc', icon: <List size={16} />, title: 'Contents' },
  ], []);

  const fetchVersions = useCallback(async () => {
    if (!workspaceId) return;
    setVersionsLoading(true);
    setVersions([]);
    setSelectedVersion(null);
    try {
      const result = await contentApi.listVersions(workspaceId, item.id);
      setVersions(result.versions);
    } catch (err) {
      console.error('Failed to load versions', err);
    } finally {
      setVersionsLoading(false);
    }
  }, [workspaceId, item.id]);

  // Refresh history when item changes while History tab is already active
  useEffect(() => {
    if (activeTab === 'history') {
      fetchVersions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id]);

  const handleTabChange = useCallback(async (id: string) => {
    setActiveTab(id as TabType);
    if (id === 'history') {
      fetchVersions();
    }
  }, [fetchVersions]);

  const handleRestoreVersion = useCallback(async (version: ContentVersion) => {
    if (!workspaceId) return;
    setRestoring(true);
    setRestoreSuccess(false);
    try {
      await contentApi.restoreVersion(workspaceId, item.id, version.id);
      setRestoreSuccess(true);
      setTimeout(() => setRestoreSuccess(false), 3000);
      onReloadItem?.();
      // Refresh version list
      const result = await contentApi.listVersions(workspaceId, item.id);
      setVersions(result.versions);
      setSelectedVersion(null);
    } catch (err) {
      console.error('Failed to restore version', err);
    } finally {
      setRestoring(false);
    }
  }, [workspaceId, item.id, onReloadItem]);

  return (
    <ReaderDetailPanelShell
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      isCollapsed={isCollapsed}
      onToggleCollapse={onToggleCollapse}
    >
      {/* Tab content */}
      {activeTab === 'properties' && (
        <PropertiesTab
          item={item}
          authors={authors}
          workspaceId={workspaceId}
          onUpdateItem={onUpdateItem}
          onDeleteItem={onDeleteItem}
          isSaving={isSaving}
        />
      )}

      {activeTab === 'social' && (
        <SocialTab
          item={item}
          authors={authors}
          socialCopies={socialCopies}
          workspaceId={workspaceId}
          projectId={projectId}
          onSaveSocialCopy={onSaveSocialCopy}
        />
      )}

      {activeTab === 'comments' && (
        <div className="p-4">
          <CommentsSection projectId={workspaceId ?? projectId} itemId={item.id} />
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="p-4">
          <ItemAnalyticsCard projectId={projectId} itemId={item.id} />
        </div>
      )}

      {activeTab === 'history' && (
        <HistoryTab
          versions={versions}
          loading={versionsLoading}
          selectedVersion={selectedVersion}
          restoring={restoring}
          restoreSuccess={restoreSuccess}
          onSelectVersion={setSelectedVersion}
          onRestoreVersion={handleRestoreVersion}
          onClearSelection={() => setSelectedVersion(null)}
        />
      )}

      {activeTab === 'toc' && (
        linkedDoc
          ? <DocOutlineContent document={linkedDoc} />
          : <div className="p-4 text-xs text-neutral-400 text-center pt-8">Open a document to see its outline.</div>
      )}
    </ReaderDetailPanelShell>
  );
}

// =============================================================================
// Properties Tab
// =============================================================================

export function PropertiesTab({
  item,
  authors,
  workspaceId,
  onUpdateItem,
  onDeleteItem,
  isSaving,
}: {
  item: ContentItem;
  authors: Author[];
  workspaceId?: string;
  onUpdateItem: (data: Partial<ContentItem>) => void;
  onDeleteItem: () => void;
  isSaving: boolean;
}) {
  const isSocialType = SOCIAL_CONTENT_TYPES.has(item.content_type);

  return (
    <div className="p-4 space-y-4">
      {/* Status */}
      <FieldGroup label="Status">
        <select
          value={item.status}
          onChange={(e) => onUpdateItem({ status: e.target.value as ContentStatus })}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
        >
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
          ))}
        </select>
      </FieldGroup>

      {/* Content Type */}
      <FieldGroup label="Content Type">
        <select
          value={item.content_type}
          onChange={(e) => onUpdateItem({ content_type: e.target.value as ContentType })}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
        >
          {ALL_CONTENT_TYPES.map((t) => (
            <option key={t} value={t}>{CONTENT_TYPE_LABELS[t]}</option>
          ))}
        </select>
      </FieldGroup>

      {/* Author */}
      <FieldGroup label="Author" icon={<User size={10} />}>
        <select
          value={item.primary_author_id || ''}
          onChange={(e) => onUpdateItem({ primary_author_id: e.target.value || null } as Partial<ContentItem>)}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
        >
          <option value="">No author</option>
          {authors.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </FieldGroup>

      <div className="h-px bg-neutral-100" />

      {/* Campaign */}
      <FieldGroup label="Campaign">
        <input
          type="text"
          defaultValue={item.campaign || ''}
          onBlur={(e) => onUpdateItem({ campaign: e.target.value || null } as Partial<ContentItem>)}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
          placeholder="e.g., Product Launch"
        />
      </FieldGroup>

      {/* Pillar */}
      <FieldGroup label="Pillar">
        <input
          type="text"
          defaultValue={item.pillar || ''}
          onBlur={(e) => onUpdateItem({ pillar: e.target.value || null } as Partial<ContentItem>)}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
          placeholder="e.g., thought-leadership"
        />
      </FieldGroup>

      <div className="h-px bg-neutral-100" />

      {/* Planned Publish Date */}
      <FieldGroup label="Planned Publish" icon={<CalIcon size={10} />}>
        <input
          type="date"
          defaultValue={item.planned_publish_date || ''}
          onBlur={(e) => onUpdateItem({ planned_publish_date: e.target.value || null } as Partial<ContentItem>)}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
        />
      </FieldGroup>

      {/* Published URL / Post URL */}
      <FieldGroup label={isSocialType ? 'Post URL' : 'Published URL'}>
        <input
          type="url"
          defaultValue={item.published_url || ''}
          onBlur={(e) => onUpdateItem({ published_url: e.target.value || null } as Partial<ContentItem>)}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20"
          placeholder="https://..."
        />
      </FieldGroup>

      {/* Distribution Schedule — inline scheduling */}
      {workspaceId && (
        <SchedulingSection
          item={item}
          authors={authors}
          workspaceId={workspaceId}
        />
      )}

      {/* Slug — hidden for social types */}
      {!isSocialType && (
        <FieldGroup label="Slug">
          <input
            type="text"
            defaultValue={item.slug || ''}
            onBlur={(e) => onUpdateItem({ slug: e.target.value || null } as Partial<ContentItem>)}
            className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 font-mono"
            placeholder="my-article-slug"
          />
        </FieldGroup>
      )}

      {!isSocialType && <div className="h-px bg-neutral-100" />}

      {/* Featured — hidden for social types */}
      {!isSocialType && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={item.is_featured}
            onChange={(e) => onUpdateItem({ is_featured: e.target.checked })}
            className="w-3.5 h-3.5 rounded border-neutral-300 text-coral-500 focus:ring-coral-500/20"
          />
          <span className="text-xs text-neutral-600 flex items-center gap-1">
            <Star size={10} className="text-amber-500" /> Featured
          </span>
        </label>
      )}

      {/* Tags */}
      <FieldGroup label="Tags" icon={<Tag size={10} />}>
        <TagInput
          value={item.tags}
          onChange={(tags) => onUpdateItem({ tags })}
          placeholder="tag1, tag2, ..."
        />
      </FieldGroup>

      {/* SEO Keywords — hidden for social types */}
      {!isSocialType && (
        <FieldGroup label="SEO Keywords">
          <TagInput
            value={item.seo_keywords}
            onChange={(keywords) => onUpdateItem({ seo_keywords: keywords })}
            placeholder="keyword1, keyword2, ..."
          />
        </FieldGroup>
      )}

      {/* Hero Image — hidden for social types */}
      {!isSocialType && (
        <FieldGroup label="Hero Image URL">
          <input
            type="url"
            defaultValue={item.hero_image_url || ''}
            onBlur={(e) => onUpdateItem({ hero_image_url: e.target.value || null } as Partial<ContentItem>)}
            className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
              focus:outline-none focus:ring-1 focus:ring-coral-500/20"
            placeholder="https://..."
          />
        </FieldGroup>
      )}

      {/* Meta Description — hidden for social types */}
      {!isSocialType && (
        <FieldGroup label="Meta Description">
          <textarea
            defaultValue={item.meta_description || ''}
            onBlur={(e) => onUpdateItem({ meta_description: e.target.value || null } as Partial<ContentItem>)}
            rows={2}
            className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
              focus:outline-none focus:ring-1 focus:ring-coral-500/20 resize-none"
            placeholder="Brief description for SEO..."
          />
        </FieldGroup>
      )}

      {/* Notes */}
      <FieldGroup label="Notes">
        <textarea
          defaultValue={item.notes || ''}
          onBlur={(e) => onUpdateItem({ notes: e.target.value || null } as Partial<ContentItem>)}
          rows={2}
          className="w-full text-xs bg-neutral-50 border border-neutral-200 rounded-lg px-2.5 py-1.5
            focus:outline-none focus:ring-1 focus:ring-coral-500/20 resize-none"
          placeholder="Internal notes..."
        />
      </FieldGroup>

      <div className="h-px bg-neutral-100" />

      {/* Delete */}
      <button
        onClick={onDeleteItem}
        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-red-500
          hover:bg-red-50 rounded-lg transition-colors"
      >
        <Trash2 size={12} />
        Delete content item
      </button>
    </div>
  );
}

// =============================================================================
// Scheduling Section (inline within Properties tab)
// =============================================================================

function SchedulingSection({
  item,
  authors,
  workspaceId,
}: {
  item: ContentItem;
  authors: Author[];
  workspaceId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [schedules, setSchedules] = useState<ContentSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [selectedAuthorIds, setSelectedAuthorIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState<Set<string>>(new Set());

  // Fetch schedules for this content item
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const all = await contentApi.getAllSchedules(workspaceId);
        if (!cancelled) {
          setSchedules(all.filter(s => s.content_item_id === item.id));
        }
      } catch (err) {
        console.error('Failed to fetch schedules', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, item.id]);

  const pendingCount = schedules.filter(s => s.status === 'pending' || s.status === 'executing').length;

  const toggleAuthor = (id: string) => {
    setSelectedAuthorIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSchedule = async () => {
    if (!scheduledAt || selectedAuthorIds.size === 0) return;
    setSubmitting(true);
    try {
      const schedule = await contentApi.distributeSocial(
        workspaceId,
        item.id,
        Array.from(selectedAuthorIds),
        undefined,
        new Date(scheduledAt).toISOString(),
      );
      setSchedules(prev => [schedule, ...prev]);
      setScheduledAt('');
      setSelectedAuthorIds(new Set());
    } catch (err) {
      console.error('Failed to schedule distribution', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (scheduleId: string) => {
    setCancelling(prev => new Set(prev).add(scheduleId));
    try {
      const updated = await contentApi.cancelSchedule(workspaceId, scheduleId);
      setSchedules(prev => prev.map(s => s.id === scheduleId ? updated : s));
    } catch (err) {
      console.error('Failed to cancel schedule', err);
    } finally {
      setCancelling(prev => {
        const next = new Set(prev);
        next.delete(scheduleId);
        return next;
      });
    }
  };

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-neutral-50
          hover:bg-neutral-100 transition-colors"
      >
        <span className="flex items-center gap-1.5 text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
          <Clock size={10} />
          Distribution Schedule
          {pendingCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[9px] font-bold bg-amber-100 text-amber-700 rounded-full normal-case tracking-normal">
              {pendingCount}
            </span>
          )}
        </span>
        {expanded ? <ChevronDown size={12} className="text-neutral-400" /> : <ChevronRight size={12} className="text-neutral-400" />}
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-2.5">
          {/* Schedule form */}
          <div className="pt-2 space-y-2">
            <input
              type="datetime-local"
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full text-xs bg-white border border-neutral-200 rounded-lg px-2.5 py-1.5
                focus:outline-none focus:ring-1 focus:ring-coral-500/20"
            />

            {authors.length > 0 && (
              <div className="space-y-1">
                <span className="text-[10px] text-neutral-500">Authors</span>
                <div className="flex flex-wrap gap-1">
                  {authors.map(a => (
                    <label key={a.id} className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedAuthorIds.has(a.id)}
                        onChange={() => toggleAuthor(a.id)}
                        className="w-3 h-3 rounded border-neutral-300 text-coral-500 focus:ring-coral-500/20"
                      />
                      <span className="text-[10px] text-neutral-600">{a.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={handleSchedule}
              disabled={submitting || !scheduledAt || selectedAuthorIds.size === 0}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium
                bg-coral-500 text-white hover:bg-coral-600 rounded-lg transition-colors
                disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Schedule Distribution
            </button>
          </div>

          {/* Existing schedules */}
          {loading && (
            <div className="flex items-center justify-center py-3">
              <Loader2 size={14} className="animate-spin text-neutral-300" />
            </div>
          )}

          {!loading && schedules.length === 0 && (
            <p className="text-[10px] text-neutral-400 text-center py-2">No schedules yet</p>
          )}

          {!loading && schedules.length > 0 && (
            <div className="space-y-1.5">
              {schedules.map(s => (
                <div key={s.id} className="bg-neutral-50 rounded-md p-2 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${SCHEDULE_STATUS_COLORS[s.status]}`}>
                      {SCHEDULE_STATUS_LABELS[s.status]}
                    </span>
                    <span className="text-[9px] text-neutral-400">
                      {new Date(s.scheduled_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-[9px] text-neutral-500">
                      {s.target_author_ids.length} author{s.target_author_ids.length !== 1 ? 's' : ''}
                    </span>
                    {s.status === 'pending' && (
                      <button
                        onClick={() => handleCancel(s.id)}
                        disabled={cancelling.has(s.id)}
                        className="flex items-center gap-0.5 text-[9px] text-neutral-400 hover:text-red-500
                          transition-colors disabled:opacity-40"
                      >
                        {cancelling.has(s.id) ? <Loader2 size={9} className="animate-spin" /> : <Ban size={9} />}
                        Cancel
                      </button>
                    )}
                  </div>

                  {s.error_message && (
                    <p className="text-[9px] text-red-500 break-words">{s.error_message}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Social Tab
// =============================================================================

const PLATFORMS = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'twitter', label: 'Twitter/X' },
  { id: 'newsletter', label: 'Newsletter' },
] as const;

const PLATFORM_CHAR_HINTS: Record<string, number> = {
  linkedin: 3000,
  twitter: 280,
  newsletter: 2000,
};

export function SocialTab({
  item,
  authors,
  socialCopies,
  workspaceId,
  projectId,
  onSaveSocialCopy,
}: {
  item: ContentItem;
  authors: Author[];
  socialCopies: SocialCopy[];
  workspaceId?: string;
  projectId: string;
  onSaveSocialCopy: (authorId: string, platform: string, text: string) => void;
}) {
  const [activePlatform, setActivePlatform] = useState('linkedin');
  const [generating, setGenerating] = useState<Record<string, boolean>>({});
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [customInstructions, setCustomInstructions] = useState<Record<string, string>>({});
  const [showCustom, setShowCustom] = useState<Record<string, boolean>>({});

  const slotKey = (authorId: string, platform: string) => `${authorId}-${platform}`;

  const handleGenerate = useCallback(async (authorId: string, platform: string) => {
    if (!workspaceId) return;
    const key = slotKey(authorId, platform);
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      const result = await contentApi.generateSocialCopy(workspaceId, {
        content_item_id: item.id,
        author_id: authorId,
        platform,
        custom_instruction: customInstructions[key] || undefined,
        project_id: projectId,
      });
      setPreviews(prev => ({ ...prev, [key]: result.generated_text }));
    } catch (err) {
      console.error('Generation failed', err);
    } finally {
      setGenerating(prev => ({ ...prev, [key]: false }));
    }
  }, [workspaceId, item.id, projectId, customInstructions]);

  const handleAccept = useCallback((authorId: string, platform: string) => {
    const key = slotKey(authorId, platform);
    const text = previews[key];
    if (!text) return;
    onSaveSocialCopy(authorId, platform, text);
    setPreviews(prev => { const next = { ...prev }; delete next[key]; return next; });
  }, [previews, onSaveSocialCopy]);

  const handleDiscard = useCallback((authorId: string, platform: string) => {
    const key = slotKey(authorId, platform);
    setPreviews(prev => { const next = { ...prev }; delete next[key]; return next; });
  }, []);

  const handleGenerateAll = useCallback(async () => {
    if (!workspaceId || authors.length === 0) return;
    const keys = authors.map(a => slotKey(a.id, activePlatform));
    setGenerating(prev => {
      const next = { ...prev };
      for (const k of keys) next[k] = true;
      return next;
    });
    try {
      const result = await contentApi.generateSocialCopyBatch(workspaceId, {
        content_item_id: item.id,
        author_ids: authors.map(a => a.id),
        platforms: [activePlatform],
        project_id: projectId,
      });
      setPreviews(prev => {
        const next = { ...prev };
        for (const r of result.results) next[slotKey(r.author_id, r.platform)] = r.generated_text;
        return next;
      });
    } catch (err) {
      console.error('Batch generation failed', err);
    } finally {
      setGenerating(prev => {
        const next = { ...prev };
        for (const k of keys) next[k] = false;
        return next;
      });
    }
  }, [workspaceId, item.id, projectId, authors, activePlatform]);

  if (authors.length === 0) {
    return (
      <div className="p-4 text-center">
        <Share2 size={24} className="mx-auto text-neutral-200 mb-2" />
        <p className="text-xs text-neutral-400">
          No authors yet. Add authors to create social copy.
        </p>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-2.5">
      {/* Platform tabs + Generate All */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider">
          Social Copy
        </p>
        {item.document_id && (
          <button
            onClick={handleGenerateAll}
            disabled={Object.values(generating).some(Boolean)}
            className="flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium
              bg-violet-50 text-violet-600 hover:bg-violet-100 rounded-md
              transition-colors disabled:opacity-50"
          >
            <Sparkles size={10} />
            Generate All
          </button>
        )}
      </div>

      <div className="flex gap-0.5 bg-neutral-100 rounded-md p-0.5">
        {PLATFORMS.map(p => (
          <button
            key={p.id}
            onClick={() => setActivePlatform(p.id)}
            className={`flex-1 text-[10px] font-medium py-1 rounded transition-colors ${
              activePlatform === p.id
                ? 'bg-white text-neutral-800 shadow-sm'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {authors.map((author) => {
        const key = slotKey(author.id, activePlatform);
        const copy = socialCopies.find(
          sc => sc.author_id === author.id && sc.platform === activePlatform
        );
        const isGenerating = generating[key] || false;
        const preview = previews[key];
        const hasPreview = preview !== undefined;
        const displayText = hasPreview ? preview : (copy?.copy_text || '');
        const charHint = PLATFORM_CHAR_HINTS[activePlatform] || 3000;

        return (
          <div
            key={`${author.id}-${activePlatform}`}
            className="bg-neutral-50 rounded-lg border border-neutral-100 p-2.5"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-coral-100 flex items-center justify-center text-[9px] font-bold text-coral-600">
                  {author.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-[11px] font-medium text-neutral-700">
                  {author.name}
                </span>
              </div>
              <div className="flex items-center gap-0.5">
                <button
                  onClick={() => setShowCustom(prev => ({ ...prev, [key]: !prev[key] }))}
                  className="text-neutral-400 hover:text-neutral-600 p-0.5 rounded transition-colors"
                  title="Custom instruction"
                >
                  <Settings size={11} />
                </button>
                <button
                  onClick={() => handleGenerate(author.id, activePlatform)}
                  disabled={isGenerating || !item.document_id}
                  title={!item.document_id ? 'Link a document first' : 'Generate with AI'}
                  className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium
                    bg-violet-50 text-violet-600 hover:bg-violet-100 rounded
                    transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isGenerating ? (
                    <Loader2 size={10} className="animate-spin" />
                  ) : (
                    <Sparkles size={10} />
                  )}
                  Generate
                </button>
              </div>
            </div>

            {showCustom[key] && (
              <input
                type="text"
                value={customInstructions[key] || ''}
                onChange={e => setCustomInstructions(prev => ({ ...prev, [key]: e.target.value }))}
                className="w-full text-[10px] bg-white border border-neutral-200 rounded px-2 py-1 mb-1.5
                  focus:outline-none focus:ring-1 focus:ring-violet-500/20 focus:border-violet-300"
                placeholder="e.g., 'Focus on ROI metrics'"
              />
            )}

            <textarea
              key={`${key}-${hasPreview ? 'preview' : 'saved'}`}
              defaultValue={displayText}
              onBlur={(e) => {
                if (hasPreview) return;
                onSaveSocialCopy(author.id, activePlatform, e.target.value);
              }}
              rows={activePlatform === 'twitter' ? 3 : 4}
              readOnly={hasPreview}
              className={`w-full text-[11px] border rounded-lg px-2 py-1.5
                focus:outline-none focus:ring-1 resize-none transition-colors ${
                hasPreview
                  ? 'bg-amber-50 border-amber-200 focus:ring-amber-500/20'
                  : 'bg-white border-neutral-200 focus:ring-coral-500/20'
              }`}
              placeholder={`${activePlatform === 'linkedin' ? 'LinkedIn' : activePlatform === 'twitter' ? 'Twitter/X' : 'Newsletter'} post for ${author.name}...`}
            />

            <div className="flex items-center justify-between mt-1">
              <span className={`text-[9px] ${displayText.length > charHint ? 'text-amber-600' : 'text-neutral-400'}`}>
                {displayText.length} / {charHint}
              </span>
              {hasPreview && (
                <div className="flex items-center gap-0.5">
                  <button
                    onClick={() => handleAccept(author.id, activePlatform)}
                    className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium
                      bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded transition-colors"
                  >
                    <Check size={10} /> Accept
                  </button>
                  <button
                    onClick={() => handleGenerate(author.id, activePlatform)}
                    disabled={isGenerating}
                    className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium
                      bg-neutral-100 text-neutral-600 hover:bg-neutral-200 rounded transition-colors disabled:opacity-50"
                  >
                    <RotateCcw size={10} /> Redo
                  </button>
                  <button
                    onClick={() => handleDiscard(author.id, activePlatform)}
                    className="flex items-center gap-0.5 px-1 py-0.5 text-[10px]
                      text-neutral-400 hover:text-neutral-600 rounded transition-colors"
                  >
                    <X size={10} />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// History Tab
// =============================================================================

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function HistoryTab({
  versions,
  loading,
  selectedVersion,
  restoring,
  restoreSuccess,
  onSelectVersion,
  onRestoreVersion,
  onClearSelection,
}: {
  versions: ContentVersion[];
  loading: boolean;
  selectedVersion: ContentVersion | null;
  restoring: boolean;
  restoreSuccess: boolean;
  onSelectVersion: (v: ContentVersion) => void;
  onRestoreVersion: (v: ContentVersion) => void;
  onClearSelection: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={18} className="animate-spin text-neutral-300" />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="p-6 text-center">
        <Clock size={24} className="mx-auto text-neutral-200 mb-2" />
        <p className="text-xs text-neutral-400">No version history yet.</p>
        <p className="text-[10px] text-neutral-300 mt-1">Versions are saved on each edit.</p>
      </div>
    );
  }

  if (selectedVersion) {
    const snap = selectedVersion.snapshot;
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-neutral-100">
          <button
            onClick={onClearSelection}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <ChevronRight size={14} className="rotate-180" />
          </button>
          <span className="text-xs font-medium text-neutral-700 flex-1">
            Version {selectedVersion.version_number}
          </span>
          <span className="text-[10px] text-neutral-400">
            {formatRelativeTime(selectedVersion.created_at)}
          </span>
        </div>

        {/* Snapshot preview */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {snap && (
            <>
              <div>
                <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">Title</p>
                <p className="text-sm font-medium text-neutral-800">{snap.title}</p>
              </div>

              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[snap.status]}`}>
                  {STATUS_LABELS[snap.status]}
                </span>
              </div>

              {snap.meta_description && (
                <div>
                  <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">Meta Description</p>
                  <p className="text-xs text-neutral-600">{snap.meta_description}</p>
                </div>
              )}

              {snap.tags && snap.tags.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {snap.tags.map(t => (
                      <span key={t} className="text-[10px] bg-neutral-100 text-neutral-600 px-1.5 py-0.5 rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedVersion.change_summary && (
                <div>
                  <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">Change Summary</p>
                  <p className="text-xs text-neutral-600">{selectedVersion.change_summary}</p>
                </div>
              )}

              {selectedVersion.created_by && (
                <div>
                  <p className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">Saved By</p>
                  <p className="text-xs text-neutral-600">{selectedVersion.created_by}</p>
                </div>
              )}
            </>
          )}

          {!snap && (
            <p className="text-xs text-neutral-400 text-center py-4">
              Snapshot not available for this version.
            </p>
          )}
        </div>

        {/* Restore footer */}
        <div className="px-3 pb-3">
          {restoreSuccess && (
            <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-xs mb-2">
              <Check size={12} />
              Restored successfully
            </div>
          )}
          <button
            onClick={() => onRestoreVersion(selectedVersion)}
            disabled={restoring}
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium
              bg-neutral-800 text-white hover:bg-neutral-900 rounded-lg transition-colors
              disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {restoring ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RotateCcw size={12} />
            )}
            Restore this version
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 space-y-1.5">
      {versions.map((v) => (
        <button
          key={v.id}
          onClick={() => onSelectVersion(v)}
          className="w-full text-left bg-neutral-50 hover:bg-neutral-100 border border-neutral-100
            hover:border-neutral-200 rounded-lg px-3 py-2.5 transition-colors group"
        >
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-xs font-medium text-neutral-700">
              v{v.version_number}
            </span>
            <span className="text-[10px] text-neutral-400">
              {formatRelativeTime(v.created_at)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-neutral-500 truncate flex-1">
              {v.change_summary || 'No summary'}
            </span>
            <ChevronRight size={12} className="text-neutral-300 group-hover:text-neutral-500 ml-1 flex-shrink-0 transition-colors" />
          </div>
          {v.created_by && (
            <p className="text-[9px] text-neutral-400 mt-0.5">{v.created_by}</p>
          )}
        </button>
      ))}
    </div>
  );
}

// =============================================================================
// Field Group helper
// =============================================================================

function FieldGroup({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-[10px] font-semibold text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1 leading-none">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}

export default ContentMetadataSidebar;
